/**
 * BoltSkillEffect — Lightning tree fork-2 active: focused single-target strike.
 *
 * The sniper of the Arc path. No chains, no spread — just one target absorbing
 * the full force of a focused discharge. Highest single-target damage in the
 * arc path, balanced by a long cooldown. Consumes Static charge for bonus
 * damage on hit.
 *
 * Bolt's upgrade chain is self-contained:
 * - Precision adds damageMultiplier (×1.35) and stunDurationMs (+400ms)
 * - Ionized adds rodChance/rodDurationMs (lightning rod marking)
 * - Conducted adds conductiveFieldDurationMs (zone at impact point)
 *
 * Key design: Bolt has NO stun at base. Stun only fires when
 * `resolver.getAdditiveEffect('bolt', 'stunDurationMs') > 0`, which requires
 * the Precision upgrade. Similarly, rod marking and conductive field zones
 * are gated behind their respective upgrade resolver values being non-zero.
 *
 * Upgrade keys consumed via UpgradeEffectResolver (scoped to 'bolt'):
 * - `damageMultiplier` (multiplicative) — ×1.35 from Precision
 * - `stunDurationMs` (additive) — +400ms from Precision
 * - `rodChance` (additive) — 0.25 from Ionized (probability per hit)
 * - `rodDurationMs` (additive) — 4000ms from Ionized
 * - `conductiveFieldDurationMs` (additive) — 1000ms from Conducted
 *
 * Fizzle behavior: cooldown consumed on fizzle. Out-of-range does not consume.
 *
 * CC hierarchy: stun checks `isImmune('stun')` before applying.
 * Damage always lands regardless of stun immunity.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md § Bolt
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BoltDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stacks: number;
    }): void;
    isImmune(effectId: string): boolean;
  };
  /** Consume all charge for the given ID. Returns amount consumed. */
  readonly consumeCharge: (chargeId: string) => number;
  /** Returns entity ID of nearest enemy within range, or null if none. */
  readonly getTarget: (rangeTiles: number) => string | null;
  /** Returns position of the target entity (used for conductive field placement). */
  readonly getTargetPosition: (targetId: string) => { x: number; y: number };
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Returns true if a probability roll succeeds (0–1 range). */
  readonly rollChance: (chance: number) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /** Deal direct (non-DoT) hit damage to a single target. */
  readonly dealDamage: (targetId: string, damage: number) => void;
  /** Spawn a zone at a position with the given type, radius, and duration. */
  readonly spawnZone: (config: {
    type: string;
    x: number;
    y: number;
    radiusTiles: number;
    durationMs: number;
  }) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BoltConfig {
  /** Cooldown in ms after activation or fizzle. Default: 2000 (long tier). */
  readonly cooldownMs: number;
  /** Base direct damage. Default: 15 (highest single-target in arc path). */
  readonly baseDamage: number;
  /** Base bolt range in tiles. Default: 4 (medium-long). */
  readonly baseRangeTiles: number;
  /** Ratio of consumed charge converted to bonus damage. Default: 0.1. */
  readonly chargeBonusDamageRatio: number;
  /** Charge ID to consume from ChargeSystem. Default: 'static_charge'. */
  readonly chargeId: string;
  /** Radius of the conductive field zone in tiles. Default: 1. */
  readonly conductiveFieldRadiusTiles: number;
}

const DEFAULT_CONFIG: BoltConfig = {
  cooldownMs: 2000,                   // long cooldown tier
  baseDamage: 15,                     // highest single-target in arc path
  baseRangeTiles: 4,                  // medium-long range
  chargeBonusDamageRatio: 0.1,        // matches Static passive default
  chargeId: 'static_charge',
  conductiveFieldRadiusTiles: 1,      // 1-tile radius conductive field
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BoltSkillEffect implements SkillEffect {
  readonly skillId = 'bolt';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: BoltDeps;
  private readonly config: BoltConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _outOfRangeAttempts = 0;
  private _zoneSpawns = 0;

  constructor(deps: BoltDeps, config: Partial<BoltConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get outOfRangeAttempts(): number { return this._outOfRangeAttempts; }
  get zoneSpawns(): number { return this._zoneSpawns; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Range check — no target in range = no cooldown consumed
    const targetId = this.deps.getTarget(this.config.baseRangeTiles);
    if (targetId === null) {
      this._outOfRangeAttempts++;
      return false;
    }

    // Fizzle check — cooldown IS consumed on fizzle
    if (!this.deps.rollSkillSuccess('bolt')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break hide on successful activation
    this.deps.breakHide();

    // Calculate damage: base × Precision multiplier + static charge bonus
    const damageMultiplier = this.deps.resolver.getMultiplicativeEffect('bolt', 'damageMultiplier');
    const chargeConsumed = this.deps.consumeCharge(this.config.chargeId);
    const chargeBonusDamage = chargeConsumed * this.config.chargeBonusDamageRatio;
    const resolvedDamage = (this.config.baseDamage * damageMultiplier) + chargeBonusDamage;

    // Deal direct damage (always lands regardless of stun immunity)
    this.deps.dealDamage(targetId, resolvedDamage);

    // Conditional stun — only fires when Precision is unlocked (stunDurationMs > 0)
    const stunDurationMs = this.deps.resolver.getAdditiveEffect('bolt', 'stunDurationMs');
    if (stunDurationMs > 0 && !this.deps.statusEffects.isImmune('stun')) {
      this.deps.statusEffects.apply('stun', {
        sourceSkillId: 'bolt',
        durationMs: stunDurationMs,
        intensity: 1,
        stacks: 1,
      });
    }

    // Conditional rod marking — only fires when Ionized is unlocked (rodChance > 0)
    const rodChance = this.deps.resolver.getAdditiveEffect('bolt', 'rodChance');
    if (rodChance > 0 && this.deps.rollChance(rodChance)) {
      const rodDurationMs = this.deps.resolver.getAdditiveEffect('bolt', 'rodDurationMs');
      if (rodDurationMs > 0) {
        // STUB: deferred — no lightning chain-targeting system reads this mark
        this.deps.statusEffects.apply('lightning_rod', {
          sourceSkillId: 'bolt',
          durationMs: rodDurationMs,
          intensity: 1,
          stacks: 1,
        });
      }
    }

    // Conditional conductive field — only fires when Conducted is unlocked
    const conductiveFieldDurationMs = this.deps.resolver.getAdditiveEffect('bolt', 'conductiveFieldDurationMs');
    if (conductiveFieldDurationMs > 0) {
      const targetPos = this.deps.getTargetPosition(targetId);
      this.deps.spawnZone({
        type: 'conductive_field',
        x: targetPos.x,
        y: targetPos.y,
        radiusTiles: this.config.conductiveFieldRadiusTiles,
        durationMs: conductiveFieldDurationMs,
      });
      this._zoneSpawns++;
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
